'use strict';

import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDrag } from 'react-dnd';
import moment from 'moment';
import MealTypes from '@app/constants/MealTypes';
import ImgResized from '@components/Widgets/ImgResized.react';
import { getPrimaryMeal } from '@utils/Meals';
import { isSunbasketFood } from '@utils/Sunbasket';
import { useMealRefresh } from '../hooks/useMealRefresh';
import SkipMenu from '../menus/SkipMenu.react';
import SwapMenu from '../menus/SwapMenu.react';
import PresentMenu from '../menus/PresentMenu.react';
import {
    useAssets,
    useMealPlanData,
    useMealOperations,
    useModalControls,
    useUIState,
    usePermissions,
} from '../contexts';

import './MealDraggable.scss';

/**
 * MealDraggable - Functional component version using react-dnd hooks
 *
 * This component represents a draggable meal card in the meal plan editor.
 * Uses modern React patterns including hooks and context.
 */
const MealDraggable = ({
    meals,
    hideIcons = false,
    disableDnD = false,
    profile,
    mealType,
    offset,
    imageDimensions,
    plan = null
}) => {
    // Get context values from split contexts
    const { recipes, foods } = useAssets();
    const { user, mealTypes } = useMealPlanData();
    const {
        addMeals,
        removeMeals,
        batchOperations,
        editMealBatches,
        startReplaceMeal,
        cancelGlobalCleanup,
        restoreAllDeletedMeals
    } = useMealOperations();
    const {
        showMealDetails,
    } = useModalControls();
    const {
        isMobile,
        getDefaultAddSwapSettings
    } = useUIState();
    const { inhibitSwap } = usePermissions();

    // State
    const [imageLoaded, setImageLoaded] = useState(true);

    // Setup drag source with react-dnd v11 API
    const dragSpec = {
        // The item MUST have a type property in v11
        item: {
            type: MealTypes.MEAL || 'meal',
            meals: meals
        },
        canDrag: !disableDnD,
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
        end: (item, monitor) => {
            if (!monitor.didDrop()) {
                return;
            }
        },
    };

    const [{ isDragging }, drag] = useDrag(dragSpec);

    // Use meal refresh hook
    const {
        loading,
        canUndoRefresh,
        refreshMeal,
        undoRefresh
    } = useMealRefresh({
        meals,
        recipes,
        foods,
        addMeals,
        removeMeals,
        batchOperations,
        editMealBatches,
        getDefaultAddSwapSettings,
        profile,
        user,
        mealType,
        offset,
        startReplaceMeal,
        plan,
    });

    // Memoize primary meal data to avoid recalculation on every render
    const { primary, recipe, food, titles, photos } = useMemo(() => {
        return getPrimaryMeal(meals, recipes, foods);
    }, [meals, recipes, foods]);

    const handleClickDetails = useCallback((event) => {
        event.preventDefault();
        // Find the full mealType object from the mealTypes array
        const fullMealType = mealTypes.find(mt => mt.name === mealType) || { name: mealType };
        showMealDetails({ meals, offset, mealType: fullMealType });
    }, [showMealDetails, meals, offset, mealType, mealTypes]);


    const handleImageError = useCallback(() => {
        setImageLoaded(false);
    }, []);

    const undoDelete = useCallback((event) => {
        event.stopPropagation();
        // Cancel global cleanup timer and restore ALL deleted meals
        cancelGlobalCleanup();
        restoreAllDeletedMeals();
    }, [cancelGlobalCleanup, restoreAllDeletedMeals]);

    if (meals.length === 0) {
        return null;
    }

    const isLeftover = primary && primary.meal_type === 'leftover';

    // Determine meal type for data attribute
    const mealTypeAttr = isLeftover ? 'leftover' :
                        (food ? 'food' :
                        (primary && primary.customRecipe ? 'custom' :
                        (primary && primary.product ? 'product' : 'recipe')));

    // Image selection logic - match old component behavior
    const imageProps = { width: 180, height: 127 };
    let showCardAsImage = false;

    if (['fresh'].includes(primary.meal_type) && photos?.[0]) {
        imageProps.src = photos[0].url;
        showCardAsImage = true;
    } else if (primary.meal_type === 'leftover' && photos?.[0]) {
        imageProps.src = photos[0].url;
        showCardAsImage = true;
    } else if (photos?.[0]?.isDeletable) {
        imageProps.src = photos[0].url;
        showCardAsImage = true;
    } else if (recipe?.image) {
        imageProps.src = recipe.image;
        showCardAsImage = true;
    }

    return (
        <div
            ref={drag}
            className="customizer-meal-draggable"
            data-dragging={isDragging}
            data-meal-type={mealTypeAttr}
            data-dnd-disabled={disableDnD}
            data-loading={loading}
            data-deleted={primary.deleted}
            data-is-mobile={isMobile}
            onClick={handleClickDetails}
            title={recipe?.title || food?.name || 'Meal'}
        >
            {/* Background Image */}
            {showCardAsImage && imageLoaded ? (
                <ImgResized
                    {...imageProps}
                    alt={recipe?.title || food?.name || 'Meal'}
                    className="meal-image"
                    onLoad={() => setImageLoaded(true)}
                    onError={handleImageError}
                />
            ) : (
                <div className="meal-image" />
            )}

            {/* Loading Overlay */}
            {loading && (
                <div className="image-overlay-text text-over-image">
                    <i className="feather feather-loader" />
                </div>
            )}

            {/* Meal Info */}
            <div className="meal-info">
                <div>
                    <div className="fix-ios-overflow-bug">
                        {isLeftover && <em>LEFTOVERS</em>}
                        {titles.join(' + ')}
                    </div>
                </div>
            </div>

            {/* Inline Refresh Button - positioned bottom-left */}
            {!primary.deleted &&
             primary.meal_type !== 'leftover' &&
             !(primary.food_uuid && isSunbasketFood(food)) &&
             !disableDnD && (
                <button
                    className="meal-refresh-button"
                    onClick={(e) => {
                        e.stopPropagation();
                        refreshMeal();
                    }}
                    title="Refresh meal"
                >
                    <i className="feather feather-refresh-ccw" />
                </button>
            )}

            {/* Controls - Handle deleted meals differently */}
            {primary.deleted ? (
                <footer className="undo-delete-container">
                    <button onClick={undoDelete} className="el-medium-btn el-orange-btn">Undo Delete</button>
                </footer>
            ) : (
                <footer className="meal-controls-inline">
                    {/* Hide clear and swap buttons when inhibitSwap is enabled */}
                    {!inhibitSwap && (
                        <SkipMenu
                            meals={meals}
                            primary={primary}
                        />
                    )}

                    {!inhibitSwap && (
                        <SwapMenu
                            primary={primary}
                            meals={meals}
                            onClickDetails={handleClickDetails}
                            onClickRefresh={refreshMeal}
                            loading={loading}
                            canUndoRefresh={canUndoRefresh}
                            onUndoRefresh={undoRefresh}
                        />
                    )}

                    <PresentMenu
                        offset={offset}
                        mealType={mealType}
                        meals={meals}
                        primary={primary}
                    />
                </footer>
            )}
        </div>
    );
};

MealDraggable.propTypes = {
    meals: PropTypes.array.isRequired,
    hideIcons: PropTypes.bool,
    disableDnD: PropTypes.bool,
    profile: PropTypes.object.isRequired,
    mealType: PropTypes.string.isRequired,
    offset: PropTypes.number.isRequired,
    imageDimensions: PropTypes.shape({
        width: PropTypes.number,
        height: PropTypes.number,
    }),
};

export default MealDraggable;
